import classnames from "classnames";
import { get, isEmpty } from "lodash";
import moment from "moment";
import React, { useState } from "react";
import { useSelector } from "react-redux";

import { getImageUrl } from "../../../../selectors/chat/application";
import { getMessageDeliveryStatus } from "../../../../selectors/chat/messages";
import { getMyId, getMyProfile } from "../../../../selectors/chat/profile";
import MessageAuthor, { MessageAuthorArgs } from "./MessageAuthor";
import MessageSettings from "./MessageSettings";
import MessageText from "./MessageText";
import QuotedMessage from "./QuotedMessage";
import MessageDeliveryStatus from "./MessageDeliveryStatus";
import useTooltip from "../../../../hooks/useTooltip";
import formatDate from "helpers/formatDate";
import FileAttachments from "./FileAttachments";
import ImageAttachments from "./ImageAttachments";
import { differenceInSeconds } from "date-fns";
import LinkPreview, { Link } from "components/chat/shared/LinkPreview";
import classNames from "classnames";

type MessageDateArgs = {
  date: string;
  format: string;
  className: string;
};

function MessageDate({ date, format, className }: MessageDateArgs) {
  const fullDate = moment(date).format("DD.MM.YYYY HH:mm");

  return (
    <time
      className={classnames("chatMessageDate", className)}
      dateTime={fullDate}
      title={fullDate}
    >
      {moment(date).format(format)}
    </time>
  );
}

type ChatMessageArgs = {
  id: string;
  chat_id: string;
  text: string | null;
  from: MessageAuthorArgs;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  groupedMessage: boolean;
  quoted_message: {
    from: MessageAuthorArgs;
    text: string | null;
    files?: AttachmentType[];
    images?: AttachmentType[];
  };
  files?: AttachmentType[];
  images?: AttachmentType[];
  link_preview?: Link;
};

export type AttachmentType = {
  content_type: string;
  extension: string;
  id: string;
  name: string | null;
  original_filename: string;
  created_at: string;
  size: number;
};

type TranslatedMessage = {
  text: string;
  quoted_message_text?: string;
};

export default function ChatMessage({
  id,
  chat_id,
  text,
  from,
  created_at,
  updated_at,
  deleted_at,
  groupedMessage,
  quoted_message,
  files,
  images,
  link_preview,
}: ChatMessageArgs) {
  const myId = useSelector((state) => getMyId(state));
  const myProfile = useSelector((state) => getMyProfile(state));
  const deliveryStatus = useSelector((state) =>
    getMessageDeliveryStatus(state, { id, chatId: chat_id }),
  );
  const myMessage = from.id === myId;

  const fromProfile = myMessage ? myProfile : from;
  const imageUrl = useSelector((state) =>
    getImageUrl(state, get(fromProfile, "image_id"), "80x80"),
  );
  const isUpdated =
    differenceInSeconds(new Date(updated_at), new Date(created_at)) > 1;
  const isDeleted = !!deleted_at;

  const tooltipRef = useTooltip<HTMLDivElement>();

  const [translated, setTranslated] = useState<{
    text: string;
    quoted_message_text: string;
    isTranslated: boolean;
  }>({
    text: text ?? "",
    quoted_message_text: quoted_message?.text ?? "",
    isTranslated: false,
  });

  const resetTranslation = () => {
    setTranslated({
      text: text ?? "",
      quoted_message_text: quoted_message?.text ?? "",
      isTranslated: false,
    });
  };

  const translateObject = (data: TranslatedMessage) => {
    setTranslated({
      ...translated,
      text: data.text,
      quoted_message_text: data.quoted_message_text
        ? data.quoted_message_text
        : quoted_message?.text ?? "",
      isTranslated: true,
    });
  };

  return (
    <div
      className={classnames(
        "chat__message group hover:relative focus:relative",
        {
          "chat__message--my": myMessage,
          "chat__message--grouped": groupedMessage,
          "chat__message--unread": !myMessage && deliveryStatus === "received",
        },
      )}
    >
      <div className="chat__bubble_arrow" />
      <div className="chat__message_header flex gap-2 mb-1 items-center">
        <MessageAuthor {...fromProfile} imageUrl={imageUrl} />
      </div>
      <div
        className={classnames({
          "pt-1.5":
            !isEmpty(quoted_message) || !isEmpty(files) || !isEmpty(images),
        })}
      >
        <div className="flex flex-col gap-2">
          {!isEmpty(quoted_message) && (
            <QuotedMessage
              from={quoted_message.from}
              text={translated.quoted_message_text || quoted_message.text}
              files={quoted_message.files}
              images={quoted_message.images}
            />
          )}
          {files && <FileAttachments files={files} chatId={chat_id} />}
          {images && (
            <ImageAttachments images={images} chatId={chat_id} id={id} />
          )}
          {link_preview && (
            <div
              className={classNames("grid grid-cols-1 bg-white rounded mt-2", {
                "grid-cols-[auto_1fr]": !!link_preview.image_url,
              })}
            >
              <LinkPreview link={link_preview} />
            </div>
          )}
        </div>
      </div>
      <MessageText
        text={translated.isTranslated ? translated.text : text}
        isUpdated={isUpdated}
        isDeleted={isDeleted}
        translated={translated.isTranslated}
      />
      <div className="chat__message_meta text-xs text-muted flex gap-1 items-center">
        <MessageDate date={created_at} format="HH:mm" className="time" />
        {isUpdated && !isDeleted && (
          <span
            ref={tooltipRef}
            title={formatDate(
              new Date(updated_at),
              I18n.t("js.time.formats.date_fns.compact_datetime"),
            )}
          >
            {" \u2022 "}
            {I18n.t("js.chat.room.message.meta.edited.title")}
            {myMessage && " \u2022 "}
          </span>
        )}
        {myMessage && <MessageDeliveryStatus deliveryStatus={deliveryStatus} />}
      </div>
      {!isDeleted && (
        <MessageSettings
          id={id}
          chatId={chat_id}
          fromId={fromProfile.id}
          fromDeleted={fromProfile.deleted}
          myMessage={myMessage}
          createdAt={created_at}
          hasFiles={!isEmpty(files)}
          className={
            "hidden group-hover:block group-focus:block absolute right-0 top-0 drop-shadow"
          }
          resetTranslation={resetTranslation}
          translateObject={translateObject}
        />
      )}
    </div>
  );
}
